/**
 * Inline approval card — Claude-Code-style.
 *
 * Shown when the meta-agent calls a `propose_*` tool. The user picks:
 *   Accept  → POST resolve(approve), card collapses to "Applied",
 *             panel auto-sends an "Approved." follow-up so the agent
 *             can continue to the next step.
 *   Edit    → Inline editor pre-filled with the proposed value;
 *             Confirm POSTs resolve(edit) and behaves like Accept.
 *   Reject  → POST resolve(reject) directly, no reason popover.
 *             The user is free to type a follow-up like "make it better
 *             with X" — the agent will propose again.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { message as antdMessage } from 'antd';
import { useLanguage } from '@hooks/useLanguage';
import type { AoAProposal, ProposalResolution } from '../types';
import { DiffView } from './DiffView';
import { AllowedModelsDiffView } from './AllowedModelsDiffView';
import { OutputFormatsDiffView } from './OutputFormatsDiffView';
import { ModelPickDiffView } from './ModelPickDiffView';
import { ModeSliderDiffView, type ModeConfigLike } from './ModeSliderDiffView';
import { TriggersDiffView, parseTriggerList } from './TriggersDiffView';
import { aoaApi } from '../services/aoaApi';
import { FileManagerProposalCard } from './FileManagerProposalCard';
import { SimulationTenantProposalCard } from './SimulationTenantProposalCard';
import { ExpandableTextarea } from '@components/common/ExpandableTextarea';
import { FilePreviewContent } from '@components/common/FilePreviewContent';
import api from '@services/api';
import { SkillIcon, BundleIcon, LibraryFolderIcon } from '../entityIcons';
import {
  linkKindLabelOf,
  statusLabelOf,
  cardTitleOf,
  cardCategoryOf,
  fieldLabelOf,
} from '../proposalLabels';
import './ProposalCard.css';

interface ProposalCardProps {
  proposal: AoAProposal;
  /** Called after a successful resolve so the parent can auto-continue
   *  (e.g. send "Approved." to nudge the agent to the next step). */
  onResolved?: (resolution: ProposalResolution, updated: AoAProposal) => void;
}

// ---------------------------------------------------------------------------
// Structured payload rendering — production layouts instead of raw JSON.
// Every proposal family gets a purpose-built body; the labeled key-value
// list is the last-resort fallback for tools added later.
// ---------------------------------------------------------------------------

const ICON_AGENT = (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor"
       strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="11" width="18" height="10" rx="2" />
    <circle cx="12" cy="5" r="2" />
    <path d="M12 7v4" />
    <line x1="8" y1="16" x2="8" y2="16" />
    <line x1="16" y1="16" x2="16" y2="16" />
  </svg>
);
// Same symbols the rest of the product uses for these entities.
const ICON_SKILL = <SkillIcon />;
const ICON_BUNDLE = <BundleIcon />;
const ICON_FOLDER = <LibraryFolderIcon />;
const ICON_FILE = (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none"
       stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <polyline points="14 2 14 8 20 8" />
  </svg>
);
const ICON_COPY = (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor"
       strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
  </svg>
);
const ICON_WARN = (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor"
       strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
    <line x1="12" y1="9" x2="12" y2="13" />
    <line x1="12" y1="17" x2="12.01" y2="17" />
  </svg>
);

/** One glyph per action family — the tinted square in the card header.
 *  A glance answers "what KIND of ask is this?" before a word is read:
 *  plus = something new, pencil = a change, link = a connection,
 *  broken link = a disconnection, file-arrow = files moving, trash = removal.
 */
const CATEGORY_ICONS: Record<string, React.ReactNode> = {
  create: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
         strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 5v14M5 12h14" />
    </svg>
  ),
  edit: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
         strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5z" />
    </svg>
  ),
  connect: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
         strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
      <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
    </svg>
  ),
  disconnect: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
         strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
      <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
      <path d="M4 4l16 16" />
    </svg>
  ),
  files: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
         strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <path d="M14 2v6h6" />
      <path d="M12 18v-6M9 15l3 3 3-3" />
    </svg>
  ),
  delete: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
         strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2m3 0v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6" />
    </svg>
  ),
};

/** Humanize a snake_case payload key for the fallback key-value list. */
function prettyKey(k: string): string {
  return k.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

/**
 * Render a payload prose field (e.g. activation_prompt) as plain text.
 * The AoA occasionally sends the value JSON-quoted ("\"Use when…\"") —
 * unwrap that so the card shows prose, never quoted JSON.
 */
function plainProse(v: unknown): string {
  if (typeof v !== 'string') return '';
  let s = v.trim();
  if (s.startsWith('"') && s.endsWith('"')) {
    try {
      const parsed = JSON.parse(s);
      if (typeof parsed === 'string') s = parsed.trim();
    } catch {
      /* keep the raw text */
    }
  }
  return s;
}

/** The FINISHED file a Library-edit card proposes — previewed as the real
 *  document (Excel/Word/text) with a download, instead of a red/green diff.
 *
 *  The bytes come from the proposal-preview endpoint, which runs the exact
 *  apply path without saving, so this is byte-for-byte what Accept writes.
 *  If the edit can no longer apply (the file changed underneath, lost access),
 *  the endpoint says so and we show that reason rather than a broken preview. */
function LibraryEditPreview({
  proposalId, fileName, t,
}: { proposalId: string; fileName: string; t: (k: string, o?: Record<string, unknown>) => string }) {
  const previewUrl = `/agent-of-agents/proposals/${proposalId}/preview`;
  const download = async () => {
    try {
      const res = await api.get(previewUrl, { responseType: 'blob' });
      const url = window.URL.createObjectURL(res.data as Blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName || 'file';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      antdMessage.error(t('metaAgent.proposal.previewDownloadFailed'));
    }
  };
  return (
    <div className="aoa-pcard-edit-preview" data-testid="aoa-pcard-edit-preview">
      <div className="aoa-pcard-edit-preview-bar">
        <span className="aoa-pcard-edit-preview-label">
          {t('metaAgent.proposal.newVersionPreview')}
        </span>
        <button
          type="button"
          className="aoa-pcard-edit-preview-download"
          onClick={download}
          data-testid="aoa-pcard-edit-preview-download"
        >
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor"
               strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
            <path d="M7 10l5 5 5-5M12 15V3" />
          </svg>
          {t('metaAgent.proposal.download')}
        </button>
      </div>
      <div className="aoa-pcard-edit-preview-body">
        <FilePreviewContent
          previewUrl={previewUrl}
          pagesUrl={`/agent-of-agents/proposals/${proposalId}/preview-pages`}
          fileName={fileName || 'file'}
        />
      </div>
    </div>
  );
}

/** Read-only full-text viewer for a long field (a whole system prompt), in the
 *  same large centred modal the Edit expander uses — so a reviewer can read the
 *  proposed prompt the way it was written, not through a cramped diff pane.
 *  Reuses ExpandableTextarea's .expand-* chrome for one consistent look. */
function PromptViewModal({
  title, text, onClose,
}: { title: string; text: string; onClose: () => void }) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);
  return createPortal(
    <div className="expand-overlay" onClick={onClose}>
      <div className="expand-modal" onClick={(e) => e.stopPropagation()}>
        <div className="expand-modal-header">
          <h3 className="expand-modal-title">{title}</h3>
          <div className="expand-modal-actions">
            <button type="button" className="expand-btn-cancel" onClick={onClose}>
              {/* Close, not Cancel — nothing is being edited here. */}
              ✕
            </button>
          </div>
        </div>
        <pre className="expand-modal-textarea aoa-pcard-prompt-view" dir="auto">{text}</pre>
      </div>
    </div>,
    document.body,
  );
}

/** Entity preview for create-agent / create-skill / create-bundle cards. */
function CreatePreview({ toolName, payload, t }: { toolName: string; payload: Record<string, any>; t: (k: string, o?: Record<string, unknown>) => string }) {
  const kind =
    toolName === 'propose_create_agent' ? 'agent'
    : toolName === 'propose_create_skill' ? 'skill'
    : toolName === 'propose_create_library_folder' ? 'library_folder'
    : 'data_bundle';
  const icon =
    kind === 'agent' ? ICON_AGENT
    : kind === 'skill' ? ICON_SKILL
    : kind === 'library_folder' ? ICON_FOLDER
    : ICON_BUNDLE;
  const name = payload.chatbot_name || payload.skill_name || payload.bundle_name
    || payload.folder_name || t('metaAgent.proposal.unnamed');
  const description =
    payload.chatbot_description || payload.skill_description || payload.bundle_description || '';
  const category = toolName === 'propose_create_skill' ? payload.skill_category : null;
  const contextPrompt =
    toolName === 'propose_create_data_bundle' ? (payload.bundle_context_prompt || '').trim() : '';
  // Skill extras: trigger keywords render as neutral label chips (same
  // pill grammar as the Edit-Skill trigger tags), NEVER as a raw JSON
  // array; the activation prompt renders as plain prose, unquoted.
  const skillTriggers =
    toolName === 'propose_create_skill' ? parseTriggerList(payload.skill_triggers) || [] : [];
  const activationPrompt =
    toolName === 'propose_create_skill' ? plainProse(payload.activation_prompt) : '';
  // Library folders: show WHERE the folder will live. parent_name is set by
  // the propose tool when a parent was picked; absent means top level.
  const folderParent =
    kind === 'library_folder'
      ? (payload.parent_name || t('metaAgent.proposal.topLevelFolder'))
      : null;
  return (
    <div className="aoa-pcard-entity" data-testid="aoa-pcard-create-preview">
      <div className="aoa-pcard-entity-head">
        <span className={`aoa-pcard-entity-icon aoa-pcard-entity-icon--${kind === 'data_bundle' || kind === 'library_folder' ? 'bundle' : kind}`} aria-hidden>
          {icon}
        </span>
        <div className="aoa-pcard-entity-title">
          <div className="aoa-pcard-entity-name">{name}</div>
          <div className="aoa-pcard-entity-kind">
            {t('metaAgent.proposal.newEntity', { kind: linkKindLabelOf(t, kind) })}
          </div>
        </div>
        {category && <span className="aoa-pcard-chip">{category}</span>}
        {folderParent && <span className="aoa-pcard-chip">{folderParent}</span>}
      </div>
      {description && <div className="aoa-pcard-entity-desc">{description}</div>}
      {contextPrompt && (
        <div className="aoa-pcard-entity-block">
          <div className="aoa-pcard-entity-block-label">{t('metaAgent.proposal.contextPrompt')}</div>
          <div className="aoa-pcard-entity-block-text">{contextPrompt}</div>
        </div>
      )}
      {activationPrompt && (
        <div className="aoa-pcard-entity-block">
          <div className="aoa-pcard-entity-block-label">{t('metaAgent.proposal.activationPrompt')}</div>
          <div className="aoa-pcard-entity-block-text">{activationPrompt}</div>
        </div>
      )}
      {skillTriggers.length > 0 && (
        <div className="aoa-pcard-entity-block" data-testid="aoa-pcard-create-triggers">
          <div className="aoa-pcard-entity-block-label">{t('metaAgent.proposal.triggerKeywords')}</div>
          <div className="aoa-pcard-chips">
            {skillTriggers.map((keyword) => (
              <span key={keyword} className="aoa-pcard-chip">{keyword}</span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/** The shared "X → Y" visual every connection card uses: two labelled nodes
 *  (each an icon tile + name) with a connector and a verb between them, so
 *  "connect this folder to this agent" reads at a glance at any name length.
 *  `cut` draws the connector broken for a disconnect. */
function ConnectVisual({
  sourceIcon, sourceName, targetIcon, targetName, verb, cut = false, chip,
}: {
  sourceIcon: React.ReactNode; sourceName: string;
  targetIcon: React.ReactNode; targetName: string;
  verb: string; cut?: boolean; chip?: React.ReactNode;
}) {
  return (
    <div className={`aoa-pcard-connect${cut ? ' aoa-pcard-connect--cut' : ''}`}
         data-testid="aoa-pcard-connection">
      <div className="aoa-pcard-connect-node">
        <span className="aoa-pcard-connect-icon" aria-hidden>{sourceIcon}</span>
        <span dir="auto" className="aoa-pcard-connect-name" title={sourceName}>{sourceName}</span>
      </div>
      <div className="aoa-pcard-connect-link" aria-hidden>
        <span className="aoa-pcard-connect-verb">{verb}</span>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
             strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M12 5v14M6 13l6 6 6-6" />
        </svg>
      </div>
      <div className="aoa-pcard-connect-node">
        <span className="aoa-pcard-connect-icon" aria-hidden>{targetIcon}</span>
        <span dir="auto" className="aoa-pcard-connect-name" title={targetName}>{targetName}</span>
        {chip}
      </div>
    </div>
  );
}

/** Connection row for attach/detach skill/bundle cards. */
function ConnectionRow({ toolName, payload, t }: { toolName: string; payload: Record<string, any>; t: (k: string, o?: Record<string, unknown>) => string }) {
  const isDetach = toolName.startsWith('propose_detach');
  const isBundle = toolName.includes('bundle');
  const toSkill = toolName.endsWith('_to_skill') || toolName.endsWith('_from_skill');
  const subjectName = isBundle
    ? (payload.bundle_name || linkKindLabelOf(t, 'data_bundle'))
    : (payload.skill_name || linkKindLabelOf(t, 'skill'));
  const targetName = toSkill
    ? (payload.skill_name || linkKindLabelOf(t, 'skill'))
    : (payload.chatbot_name || linkKindLabelOf(t, 'agent'));
  return (
    <ConnectVisual
      sourceIcon={isBundle ? ICON_BUNDLE : ICON_SKILL}
      sourceName={subjectName}
      targetIcon={toSkill ? ICON_SKILL : ICON_AGENT}
      targetName={targetName}
      verb={isDetach ? t('metaAgent.proposal.disconnectFrom') : t('metaAgent.proposal.connectTo')}
      cut={isDetach}
    />
  );
}

/** Duplicate-agent summary: source → copy, with option chips. */
function DuplicateRow({ payload, t }: { payload: Record<string, any>; t: (k: string, o?: Record<string, unknown>) => string }) {
  return (
    <div className="aoa-pcard-entity" data-testid="aoa-pcard-duplicate">
      <div className="aoa-pcard-entity-head">
        <span className="aoa-pcard-entity-icon aoa-pcard-entity-icon--agent" aria-hidden>
          {ICON_COPY}
        </span>
        <div className="aoa-pcard-entity-title">
          <div className="aoa-pcard-entity-name">
            {payload.source_chatbot_name || linkKindLabelOf(t, 'agent')}
            <span className="aoa-pcard-dup-arrow" aria-hidden> → </span>
            {payload.new_name || t('metaAgent.proposal.copyFallback')}
          </div>
          <div className="aoa-pcard-entity-kind">{t('metaAgent.proposal.privateDuplicate')}</div>
        </div>
      </div>
      <div className="aoa-pcard-chips">
        <span className="aoa-pcard-chip">
          {t('metaAgent.proposal.duplicateSkills', {
            mode: t(payload.duplicate_skills
              ? 'metaAgent.proposal.independentCopies'
              : 'metaAgent.proposal.sharedWithOriginal'),
          })}
        </span>
        <span className="aoa-pcard-chip">
          {t('metaAgent.proposal.duplicateBundles', {
            mode: t(payload.duplicate_data_bundles
              ? 'metaAgent.proposal.independentCopies'
              : 'metaAgent.proposal.sharedWithOriginal'),
          })}
        </span>
      </div>
    </div>
  );
}

/** Warning panel for the destructive delete/archive cards. */
function DeletePanel({ payload, t }: { payload: Record<string, any>; t: (k: string, o?: Record<string, unknown>) => string }) {
  const name = payload.agent_name || payload.skill_name || payload.bundle_name || t('metaAgent.proposal.unnamed');
  const isArchive = payload.destructive_action === 'archive';
  const facts: string[] = [];
  if (payload.linked_agent_count) facts.push(t('metaAgent.proposal.usedByAgents', { count: payload.linked_agent_count }));
  if (payload.linked_skill_count) facts.push(t('metaAgent.proposal.usedBySkills', { count: payload.linked_skill_count }));
  if (payload.file_membership_count) facts.push(t('metaAgent.proposal.filesInside', { count: payload.file_membership_count }));
  return (
    <div className="aoa-pcard-danger" data-testid="aoa-pcard-delete">
      <div className="aoa-pcard-danger-head">
        <span className="aoa-pcard-danger-icon" aria-hidden>{ICON_WARN}</span>
        <div className="aoa-pcard-entity-title">
          <div className="aoa-pcard-entity-name">{name}</div>
          <div className="aoa-pcard-danger-kind">
            {isArchive ? t('metaAgent.proposal.willBeArchived') : t('metaAgent.proposal.willBeDeleted')}
          </div>
        </div>
      </div>
      {facts.length > 0 && (
        <div className="aoa-pcard-chips">
          {facts.map((f, i) => <span key={i} className="aoa-pcard-chip aoa-pcard-chip--warn">{f}</span>)}
        </div>
      )}
      {payload.consequences && (
        <div className="aoa-pcard-danger-text">{String(payload.consequences)}</div>
      )}
    </div>
  );
}

/** Last-resort fallback: labeled key-value rows — never raw JSON. */
function KeyValueList({ payload, t }: { payload: Record<string, any>; t: (k: string, o?: Record<string, unknown>) => string }) {
  const rows = Object.entries(payload).filter(
    ([k, v]) =>
      !k.endsWith('_id') && k !== 'change_summary' && v !== null && v !== undefined &&
      (typeof v !== 'object' || Array.isArray(v)),
  );
  if (rows.length === 0) return null;
  return (
    <div className="aoa-pcard-kv" data-testid="aoa-pcard-kv">
      {rows.map(([k, v]) => (
        <div key={k} className="aoa-pcard-kv-row">
          <span className="aoa-pcard-kv-key">{prettyKey(k)}</span>
          <span className="aoa-pcard-kv-val">
            {typeof v === 'boolean' ? (v ? t('metaAgent.proposal.yes') : t('metaAgent.proposal.no'))
              : Array.isArray(v) ? v.join(', ')
              : String(v)}
          </span>
        </div>
      ))}
    </div>
  );
}

const CONNECTION_TOOLS = new Set([
  'propose_attach_skill', 'propose_detach_skill',
  'propose_attach_bundle', 'propose_detach_bundle',
  'propose_attach_bundle_to_skill', 'propose_detach_bundle_from_skill',
]);
const DELETE_TOOLS = new Set([
  'propose_delete_library_folder',
  'propose_delete_agent', 'propose_delete_skill', 'propose_delete_data_bundle',
]);

// Fields whose value is a whole document (a system prompt, the rules, the files
// context). For these the card offers a read-only "View" that opens the full
// text large and clean, and gives the inline diff more room — a system prompt
// reviewed through a cramped scroll pane is unreadable.
const LONG_TEXT_FIELDS = new Set([
  'chatbot_system_prompt', 'agent_rules', 'files_context_prompt',
]);

function stringifyValue(v: unknown): string {
  if (v === null || v === undefined) return '';
  if (typeof v === 'string') return v;
  try {
    return JSON.stringify(v, null, 2);
  } catch {
    return String(v);
  }
}

const FILE_MANAGER_TOOL_NAMES = new Set([
  'propose_manage_files_on_agent',
  'propose_manage_files_on_skill',
  'propose_manage_files_on_data_bundle',
  'propose_manage_files_on_tenant',
  'propose_manage_files_on_folder',
]);

export const ProposalCard: React.FC<ProposalCardProps> = ({ proposal, onResolved }) => {
  // File-manager proposals have their own card variant — the regular
  // Accept / Edit / Reject buttons don't fit the upload+remove workflow.
  // Dispatched BEFORE any of the regular ProposalCard state so the
  // FileManagerProposalCard owns its own busy/error/local-status state.
  //
  // Pre-fix used a CommonJS `require()` which Vite (ESM) treats as a
  // dynamic call that doesn't resolve at build-time the way native
  // imports do — the user reported the file-manager card showed
  // "Loading limits…" forever. Use a normal top-level import now.
  if (FILE_MANAGER_TOOL_NAMES.has(proposal.tool_name)) {
    return <FileManagerProposalCard proposal={proposal} onResolved={onResolved} />;
  }

  // The simulation tenant question is a picker, not an Accept/Edit/Reject of a
  // proposed value — Edit would offer a raw UUID textarea.
  if (proposal.tool_name === 'propose_simulation_tenant') {
    return <SimulationTenantProposalCard proposal={proposal} onResolved={onResolved} />;
  }

  const { t } = useLanguage();
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(false);
  // Read-only full-text view of a long field (the system prompt above all).
  const [viewingText, setViewingText] = useState(false);
  // Pre-fill the Edit textarea with whatever value the meta-agent
  // proposed, so clicking Edit lets the user TWEAK the draft rather
  // than retyping the whole thing.
  //   * For propose_edit_*  → new_value carries the proposed text.
  //   * For propose_create_* and propose_attach_* → new_value is null;
  //     the payload lives in proposed_payload (JSON dict). Pre-fill from
  //     that so the user can edit the JSON instead of starting blank.
  const [editedText, setEditedText] = useState<string>(() => {
    if (proposal.new_value !== null && proposal.new_value !== undefined) {
      return stringifyValue(proposal.new_value);
    }
    if (proposal.proposed_payload) {
      return stringifyValue(proposal.proposed_payload);
    }
    return '';
  });
  const [error, setError] = useState<string | null>(null);
  const [localStatus, setLocalStatus] = useState<AoAProposal['status']>(proposal.status);

  // Re-sync local status when the parent updates the underlying proposal —
  // happens when the panel auto-rejects pending cards because the user
  // typed a new message instead of clicking Accept / Edit / Reject, or
  // when a proposal_resolved event flows through the reducer for any
  // other reason.
  useEffect(() => {
    setLocalStatus(proposal.status);
  }, [proposal.status]);

  const isResolved = localStatus !== 'pending';
  const isEdit = proposal.field != null;
  const isCreate =
    proposal.tool_name === 'propose_create_agent' ||
    proposal.tool_name === 'propose_create_skill' ||
    proposal.tool_name === 'propose_create_data_bundle' ||
    proposal.tool_name === 'propose_create_library_folder';

  // Parsed before/after for handler_config edits, with per-key CHANGE
  // detection. The AoA often sends back the WHOLE handler_config object
  // with only one key actually different (the backend shallow-merges, so
  // both shapes are legal) — so "which specialized view do we render"
  // must key on what CHANGED, not on which keys are merely present.
  // Keying on presence is the bug that rendered a MODELS card for an
  // output_formats (artifacts) change: the untouched allowed_models key
  // rode along in new_value and won the view.
  const handlerConfigDiff = useMemo<{
    before: any; after: any; changed: Set<string>;
  } | null>(() => {
    if (proposal.tool_name !== 'propose_edit_agent') return null;
    if (proposal.field !== 'handler_config') return null;
    const parseMaybe = (v: any): any => {
      if (v == null) return null;
      if (typeof v === 'string') {
        try { return JSON.parse(v); } catch { return null; }
      }
      return v;
    };
    const after = parseMaybe(proposal.new_value);
    if (!after || typeof after !== 'object') return null;
    const before = parseMaybe(proposal.old_value);
    const beforeObj = before && typeof before === 'object' ? before : {};
    const changed = new Set<string>(
      Object.keys(after).filter(
        (k) => JSON.stringify(after[k]) !== JSON.stringify(beforeObj[k]),
      ),
    );
    return { before: beforeObj, after, changed };
  }, [proposal.tool_name, proposal.field, proposal.new_value, proposal.old_value]);

  // Edit-suppressed when the proposal touches anything model-shaped or
  // the constrained output_formats map:
  //   - chatbot_config carrying model/provider keys (default-model change)
  //   - handler_config CHANGING allowed_models (the picker list)
  //   - handler_config CHANGING output_formats (artifacts — fixed key set)
  // The user must pick Accept or Reject — they cannot free-type a model
  // id or format key in the textarea (that would let them set an
  // off-registry value and accept it). See persona §8c-prelude.
  const _MODEL_KEYS = ['model', 'model_name', 'provider', 'provider_name', 'ai_model', 'ai_provider'];
  const isModelProposal = useMemo<boolean>(() => {
    if (proposal.tool_name !== 'propose_edit_agent') return false;
    // default_model is a DIFFERENT field from handler_config.allowed_models,
    // but the same reasoning applies: the user must Accept / Reject the
    // exact id — free-typing would allow an off-registry model.
    if (proposal.field === 'default_model') return true;
    if (proposal.field === 'chatbot_config') {
      const raw = proposal.new_value;
      if (raw == null) return false;
      let parsed: any = raw;
      if (typeof raw === 'string') {
        try { parsed = JSON.parse(raw); } catch { return false; }
      }
      if (!parsed || typeof parsed !== 'object') return false;
      return _MODEL_KEYS.some((k) => k in parsed);
    }
    if (proposal.field === 'handler_config') {
      if (!handlerConfigDiff) return false;
      return handlerConfigDiff.changed.has('allowed_models')
        || handlerConfigDiff.changed.has('output_formats');
    }
    return false;
  }, [proposal.tool_name, proposal.field, proposal.new_value, handlerConfigDiff]);

  // Specialized handler_config views — rendered only when that key
  // actually CHANGED (see handlerConfigDiff above). Falls back to
  // DiffView for anything else.
  const allowedModelsChange = useMemo<{ before: any; after: any } | null>(() => {
    if (!handlerConfigDiff || !handlerConfigDiff.changed.has('allowed_models')) return null;
    return {
      before: handlerConfigDiff.before.allowed_models,
      after: handlerConfigDiff.after.allowed_models,
    };
  }, [handlerConfigDiff]);

  const outputFormatsChange = useMemo<{ before: any; after: any } | null>(() => {
    if (!handlerConfigDiff || !handlerConfigDiff.changed.has('output_formats')) return null;
    return {
      before: handlerConfigDiff.before.output_formats,
      after: handlerConfigDiff.after.output_formats,
    };
  }, [handlerConfigDiff]);

  // AI-model pick — propose_edit_agent(field='default_model') carries a
  // bare model id in new_value (old_value may be null when the agent had
  // no explicit model yet). Rendered as a model-picker-style list instead
  // of a text diff; Edit is suppressed via isModelProposal above.
  const defaultModelChange = useMemo<{ before: string | null; after: string } | null>(() => {
    if (proposal.tool_name !== 'propose_edit_agent') return null;
    if (proposal.field !== 'default_model') return null;
    const after = typeof proposal.new_value === 'string' ? proposal.new_value.trim() : '';
    if (!after) return null;
    const before =
      typeof proposal.old_value === 'string' && proposal.old_value.trim()
        ? proposal.old_value.trim()
        : null;
    return { before, after };
  }, [proposal.tool_name, proposal.field, proposal.new_value, proposal.old_value]);

  // Behavior-switch edit — proposal.new_value is a ModeConfig JSON
  // (string or object). Parsed here; a shape we can't read falls back to
  // the plain DiffView below, never a blank card.
  const modeSliderChange = useMemo<{ before: ModeConfigLike | null; after: ModeConfigLike } | null>(() => {
    if (proposal.tool_name !== 'propose_edit_agent') return null;
    if (proposal.field !== 'mode_slider_config') return null;
    const parseMaybe = (v: any): ModeConfigLike | null => {
      if (v == null) return null;
      let parsed: any = v;
      if (typeof v === 'string') {
        try { parsed = JSON.parse(v); } catch { return null; }
      }
      if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return null;
      return parsed as ModeConfigLike;
    };
    const after = parseMaybe(proposal.new_value);
    if (!after) return null;
    return { before: parseMaybe(proposal.old_value), after };
  }, [proposal.tool_name, proposal.field, proposal.new_value, proposal.old_value]);

  // Skill trigger-keywords edit — old_value / new_value carry a JSON
  // array (string or array). Rendered as label chips with added / kept /
  // removed variants (TriggersDiffView) — the user must never see the
  // raw JSON array. Any unparseable side falls back to the plain
  // DiffView below. Edit stays available for this field.
  const triggersChange = useMemo<{ before: string[]; after: string[] } | null>(() => {
    if (proposal.tool_name !== 'propose_edit_skill') return null;
    if (proposal.field !== 'skill_triggers') return null;
    const after = parseTriggerList(proposal.new_value);
    if (after === null) return null;
    const before = parseTriggerList(proposal.old_value);
    if (before === null) return null;
    return { before, after };
  }, [proposal.tool_name, proposal.field, proposal.new_value, proposal.old_value]);

  // Library file RENAME — a two-pane diff of one short name is noise, so the
  // card shows the old name struck through beside the new one. The folder is
  // named because a name belongs to a PLACEMENT: the same file can be called
  // something else in another folder, and this card changes only one of them.
  const renameChange = useMemo<
    { from: string; to: string; folder: string; kind: 'file' | 'folder' } | null
  >(() => {
    const isFile = proposal.tool_name === 'propose_rename_library_file';
    const isFolder = proposal.tool_name === 'propose_rename_library_folder';
    if (!isFile && !isFolder) return null;
    const p = (proposal.proposed_payload || {}) as Record<string, unknown>;
    const to = String(p.new_name ?? proposal.new_value ?? '');
    if (!to) return null;
    return {
      from: String(p.old_name ?? proposal.old_value ?? ''),
      to,
      // A file rename names the folder its placement lives in; a folder rename
      // is the folder itself, so it carries no parent-folder label.
      folder: isFile ? String(p.folder_name ?? '') : '',
      kind: isFolder ? 'folder' : 'file',
    };
  }, [proposal.tool_name, proposal.proposed_payload, proposal.old_value, proposal.new_value]);

  // Copy / move — a STATEMENT, not a diff. Neither changes a single character
  // of the file, so the +/- machinery had nothing true to show: it was diffing
  // the old NAME against the new one and presenting "make me a duplicate" as
  // though lines of the document were being rewritten. What a person needs to
  // check here is which file, and where it is about to land.
  const fileOpChange = useMemo<
    {
      kind: 'copy' | 'move' | 'delete'; name: string; from: string; to: string;
      forever: boolean; keptIn: string[];
    } | null
  >(() => {
    const isCopy = proposal.tool_name === 'propose_copy_library_file';
    const isMove = proposal.tool_name === 'propose_move_library_file';
    const isDelete = proposal.tool_name === 'propose_delete_library_file';
    if (!isCopy && !isMove && !isDelete) return null;
    const p = (proposal.proposed_payload || {}) as Record<string, unknown>;
    return {
      kind: isCopy ? 'copy' : isMove ? 'move' : 'delete',
      forever: Boolean(p.deleted_forever),
      keptIn: (Array.isArray(p.kept_in) ? p.kept_in : []).map(String),
      // A copy's headline is the NAME IT WILL HAVE; a move's is the file
      // itself, since a move leaves the name alone.
      name: String((isCopy ? p.new_name : p.file_name) ?? ''),
      from: String(p.folder_name ?? ''),
      to: String(p.dest_folder_name ?? ''),
    };
  }, [proposal.tool_name, proposal.proposed_payload]);

  // Which file a content edit is about, and where it lives. The diff shows
  // WHAT changes; without this the user has no way to tell WHICH document they
  // are about to rewrite — the one thing they cannot undo by rejecting later.
  const libraryEditSubject = useMemo<
    { fileName: string; folders: string[]; truncated: boolean } | null
  >(() => {
    if (proposal.tool_name !== 'propose_edit_library_file') return null;
    const p = (proposal.proposed_payload || {}) as Record<string, unknown>;
    const folders = Array.isArray(p.folders)
      ? (p.folders as Array<Record<string, unknown>>)
          .map((f) => String(f.folder_name ?? ''))
          .filter(Boolean)
      : [];
    return {
      fileName: String(p.file_name ?? ''),
      folders,
      truncated: Boolean(p.diff_truncated),
    };
  }, [proposal.tool_name, proposal.proposed_payload]);

  // An edit that CHANGES CONTENT (not a pure rename) gets the preview instead
  // of a diff. Rename carries no new bytes, so it keeps its old→new name row.
  const libraryEditEditsContent =
    proposal.tool_name === 'propose_edit_library_file';

  // Pretty, human-readable render for the file link/unlink cards — so they
  // match the polished look of the other proposal cards instead of dumping the
  // raw proposed_payload JSON.
  const linkFilePayload = useMemo<
    { action: string; targetType: string; targetName: string; fileName: string } | null
  >(() => {
    if (proposal.tool_name !== 'propose_link_file' && proposal.tool_name !== 'propose_unlink_file') {
      return null;
    }
    const p = proposal.proposed_payload as Record<string, unknown> | null;
    if (!p || typeof p !== 'object') return null;
    return {
      action: String(p.action || (proposal.tool_name === 'propose_unlink_file' ? 'unlink' : 'link')),
      targetType: String(p.target_type || ''),
      targetName: String(p.target_name || ''),
      fileName: String(p.file_name || ''),
    };
  }, [proposal.tool_name, proposal.proposed_payload]);

  // Library link/unlink + file-into-folder cards (modules.library tenants).
  // Rendered as a link row like the bundle-era file cards, plus a mode chip
  // (always in context / searchable) — the mode lives on the LINK here.
  const libraryLinkPayload = useMemo<
    {
      action: 'link' | 'unlink' | 'file';
      nodeType: string; nodeName: string;
      targetKind: string; targetName: string;
      includeAsContext: boolean | null;
    } | null
  >(() => {
    const tool = proposal.tool_name;
    if (tool !== 'propose_link_library' && tool !== 'propose_unlink_library'
        && tool !== 'propose_add_file_to_folder') {
      return null;
    }
    const p = proposal.proposed_payload as Record<string, unknown> | null;
    if (!p || typeof p !== 'object') return null;
    if (tool === 'propose_add_file_to_folder') {
      return {
        action: 'file',
        nodeType: 'file',
        nodeName: String(p.file_name || ''),
        targetKind: 'library_folder',
        targetName: String(p.folder_name || ''),
        includeAsContext: null,
      };
    }
    return {
      action: tool === 'propose_unlink_library' ? 'unlink' : 'link',
      nodeType: String(p.node_type || 'folder'),
      nodeName: String(p.node_name || ''),
      targetKind: String(p.target_type || ''),
      targetName: String(p.target_name || ''),
      includeAsContext:
        tool === 'propose_link_library' ? Boolean(p.include_as_context) : null,
    };
  }, [proposal.tool_name, proposal.proposed_payload]);

  // "What changed" — plain-language bullets the meta-agent supplies with
  // every long-text edit (edit_tools `change_summary` arg, persisted in
  // proposed_payload). Rendered above the diff so the user reads the
  // human summary FIRST and only then dives into the before/after.
  const changeSummary = useMemo<string[]>(() => {
    const p = proposal.proposed_payload as Record<string, unknown> | null;
    const raw = p && typeof p === 'object' ? p.change_summary : null;
    if (typeof raw !== 'string' || !raw.trim()) return [];
    return raw
      .split(/\r?\n/)
      .map((line) => line.replace(/^\s*[-•*]\s*/, '').trim())
      .filter(Boolean)
      .slice(0, 6);
  }, [proposal.proposed_payload]);

  const statusVariant = useMemo<'pending' | 'applied' | 'edited' | 'rejected'>(() => {
    if (localStatus === 'approved') return 'applied';
    if (localStatus === 'edited') return 'edited';
    if (localStatus === 'rejected' || localStatus === 'timeout') return 'rejected';
    return 'pending';
  }, [localStatus]);

  async function resolve(resolution: ProposalResolution) {
    setBusy(true);
    setError(null);
    try {
      const updated = await aoaApi.resolveProposal(proposal.proposal_id, resolution);
      setLocalStatus(updated.status);
      onResolved?.(resolution, updated);
    } catch (e: any) {
      // The proposal may have been resolved from another surface (chat
      // marker, another tab) — the server answers "not pending". Re-fetch
      // and flip this card into its real resolved state instead of leaving
      // live buttons + a red error line (observed 2026-07-26).
      try {
        const fresh = await aoaApi.getProposal(proposal.proposal_id);
        if (fresh.status && fresh.status !== 'pending') {
          setLocalStatus(fresh.status);
          // Report it like any other resolution. This card showing "applied"
          // while the agent says nothing more is the same stall as a dropped
          // continuation — the user made a decision and the turn has to
          // resume from it. The chat's handler is idempotent per proposal, so
          // if whoever resolved it first already continued, this is a no-op.
          onResolved?.(resolution, fresh);
          return;
        }
      } catch {
        /* fall through to the generic error */
      }
      const msg = e?.response?.data?.error || e?.message || t('metaAgent.proposal.resolveFailed');
      setError(msg);
      antdMessage.error(msg);
    } finally {
      setBusy(false);
    }
  }

  const onAccept = () => resolve({ action: 'approve' });
  const onReject = () => resolve({ action: 'reject' });

  const onConfirmEdit = () => {
    let finalValue: unknown = editedText;
    const jsonFields = new Set([
      'handler_config',
      'mode_slider_config',
      'chatbot_config',
      'skill_triggers',
      'skill_config',
      'sync_folders',
    ]);
    if (proposal.field && jsonFields.has(proposal.field)) {
      try {
        finalValue = JSON.parse(editedText);
      } catch {
        finalValue = editedText;
      }
    }
    if (isCreate) {
      try {
        const parsed = JSON.parse(editedText);
        resolve({ action: 'edit', final_value: parsed, edited_payload: parsed });
        return;
      } catch {
        /* fall through to text edit */
      }
    }
    resolve({
      action: 'edit',
      final_value: finalValue,
      edited_payload: { new_value: finalValue },
    });
  };

  const oldText = stringifyValue(proposal.old_value);
  const newText = stringifyValue(proposal.new_value);

  // Generic-proposal fallback: any non-edit, non-create proposal
  // (attach, detach, duplicate, delete, etc.) still needs to show
  // WHAT was being proposed so a re-opened resolved card isn't a
  // mysterious "Rejected." Every known tool family gets a purpose-built
  // layout; the labeled key-value list is the fallback for future tools.
  // Raw JSON never reaches the user (card redesign 2026-07-26).
  const payloadObj = (proposal.proposed_payload && typeof proposal.proposed_payload === 'object'
    ? proposal.proposed_payload
    : {}) as Record<string, any>;
  const isConnection = CONNECTION_TOOLS.has(proposal.tool_name);
  const isDuplicate = proposal.tool_name === 'propose_duplicate_agent';
  const isDelete = DELETE_TOOLS.has(proposal.tool_name);
  const showsGenericPayload =
    !isEdit && !isCreate && !linkFilePayload && !libraryLinkPayload &&
    !isConnection && !isDuplicate && !isDelete &&
    proposal.proposed_payload != null;

  const editTargetName: string | null = (() => {
    const p = (payloadObj && typeof payloadObj === 'object') ? payloadObj as Record<string, unknown> : null;
    const v = p && (p.chatbot_name || p.skill_name || p.bundle_name);
    return typeof v === 'string' && v.trim() ? v : null;
  })();

  return (
    <div
      className={`aoa-pcard aoa-pcard-status-${statusVariant}${
        isResolved ? ' aoa-pcard--resolved-view' : ''
      }`}
      data-testid="aoa-proposal-card"
      aria-disabled={isResolved || undefined}
    >
      {/* Header: category icon in a tinted square + a HUMAN title composed
          from tool + payload ("Connecting a folder to an agent"), per the
          design direction that every card must read at a glance. The raw
          tool name is gone from the title; the field chip stays because it
          is precise, short, and the thing the diff below actually edits. */}
      <div className="aoa-pcard-header">
        <div
          className={`aoa-pcard-action-icon aoa-pcard-action-icon--${cardCategoryOf(proposal.tool_name)}`}
          aria-hidden
        >
          {CATEGORY_ICONS[cardCategoryOf(proposal.tool_name)]}
        </div>
        <div className="aoa-pcard-header-text">
          <div className="aoa-pcard-title">
            <span className="aoa-pcard-title-text" dir="auto">
              {cardTitleOf(t, proposal.tool_name, payloadObj)}
            </span>
          </div>
          {/* The specific field, humanized ("System prompt", not
              chatbot_system_prompt) as a soft chip — the one precise detail the
              title can't carry. The entity-kind sub-label was redundant with the
              title and is gone; the meta-agent disambiguates multiple cards by
              naming the entity in prose (persona §2 rule 7). */}
          {proposal.field && (
            <div className="aoa-pcard-fieldchip">{fieldLabelOf(t, proposal.field)}</div>
          )}
          {/* WHICH agent / skill this edit lands on. A build of several
              agents raises several identical-looking "Model" cards; the name
              is the only thing that tells them apart. Persisted on the
              proposal at create time (edit_tools._edit_payload). */}
          {proposal.field && editTargetName && (
            <div className="aoa-pcard-fieldchip aoa-pcard-fieldchip--target" dir="auto">
              {editTargetName}
            </div>
          )}
        </div>
        {/* Class from the variant, label from the RAW status. statusLabelOf
            collapses the five stored statuses itself; feeding it the already
            collapsed variant made 'applied' and 'edited' match none of its
            branches, so an approved card fell through to "Pending" while
            wearing the green applied badge. */}
        <div className={`aoa-pcard-status aoa-pcard-status-${statusVariant}`}>
          {statusLabelOf(t, localStatus)}
        </div>
      </div>

      {/* Read-only banner — only when the user reopened a resolved
          proposal via the chat marker. Sits ABOVE the body so the
          reader registers "this is history" before they scan the
          suggestion content. */}
      {/* Resolved → small lock badge at the top-right of the card.
          The whole card body underneath gets a desaturated / faded
          treatment via .aoa-pcard--resolved-view in ProposalCard.css
          so the user reads it as "read-only" without a banner. */}
      {isResolved && (
        <div
          className="aoa-pcard-lock-badge"
          data-testid="aoa-pcard-readonly"
          title={
            statusVariant === 'rejected'
              ? t('metaAgent.proposal.rejectedReadOnly')
              : statusVariant === 'edited'
              ? t('metaAgent.proposal.appliedEditedReadOnly')
              : t('metaAgent.proposal.appliedReadOnly')
          }
          aria-label={t('metaAgent.fileManager.readOnlyResolved')}
        >
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none"
               stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
               aria-hidden="true">
            <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
            <path d="M7 11V7a5 5 0 0 1 10 0v4" />
          </svg>
        </div>
      )}

      {/* Rationale */}
      {proposal.rationale && !editing && (
        <div className="aoa-pcard-rationale">{proposal.rationale}</div>
      )}

      {/* "What changed" — the agent's plain-language summary of the edit.
          Sits between the rationale (WHY) and the diff (the raw WHAT) so a
          non-technical reviewer can approve without parsing the diff. */}
      {changeSummary.length > 0 && !editing && (
        <div className="aoa-pcard-changes" data-testid="aoa-pcard-changes">
          <div className="aoa-pcard-changes-title">
            {t('metaAgent.proposal.whatChanged')}
          </div>
          <ul className="aoa-pcard-changes-list">
            {changeSummary.map((line, i) => (
              <li key={i}>{line}</li>
            ))}
          </ul>
        </div>
      )}

      {/* Body — always show the suggestion content, regardless of
          status. A resolved card reopened from the chat marker must
          remind the user what was proposed; otherwise they only see
          "Rejected" with no context. */}
      {!editing && (
        <div className="aoa-pcard-body">
          {/* When the edit is specifically a model-list change on
              handler_config, render labeled model chips instead of
              the raw JSON DiffView. Far more readable. Falls back to
              DiffView for every other field-edit. */}
          {isEdit && allowedModelsChange && (
            <AllowedModelsDiffView
              before={allowedModelsChange.before}
              after={allowedModelsChange.after}
            />
          )}
          {isEdit && outputFormatsChange && (
            <OutputFormatsDiffView
              before={outputFormatsChange.before}
              after={outputFormatsChange.after}
            />
          )}
          {isEdit && defaultModelChange && (
            <ModelPickDiffView
              before={defaultModelChange.before}
              after={defaultModelChange.after}
            />
          )}
          {isEdit && modeSliderChange && (
            <ModeSliderDiffView
              before={modeSliderChange.before}
              after={modeSliderChange.after}
            />
          )}
          {isEdit && triggersChange && (
            <TriggersDiffView
              before={triggersChange.before}
              after={triggersChange.after}
            />
          )}
          {renameChange && (
            <div className="aoa-pcard-linkrow" data-testid="aoa-pcard-rename">
              <span className="aoa-pcard-linkrow-icon" aria-hidden dir="auto">
                {renameChange.kind === 'folder' ? ICON_FOLDER : ICON_FILE}
              </span>
              <div className="aoa-pcard-linkrow-text">
                <span className="aoa-pcard-linkrow-file">
                  {renameChange.from && (
                    <s className="aoa-pcard-rename-old">{renameChange.from}</s>
                  )}
                  {renameChange.from ? ' → ' : ''}
                  {renameChange.to}
                </span>
                <span className="aoa-pcard-linkrow-sub">
                  {renameChange.kind === 'folder'
                    ? t('metaAgent.proposal.renameFolder')
                    : renameChange.folder
                      ? t('metaAgent.proposal.renameInFolder', { folder: renameChange.folder })
                      : t('metaAgent.proposal.renameFile')}
                </span>
              </div>
            </div>
          )}
          {fileOpChange && (
            <div className="aoa-pcard-linkrow" data-testid="aoa-pcard-fileop">
              <span className="aoa-pcard-linkrow-icon" aria-hidden>{ICON_FILE}</span>
              <div className="aoa-pcard-linkrow-text">
                <span className="aoa-pcard-linkrow-file" dir="auto">{fileOpChange.name}</span>
                <span className="aoa-pcard-linkrow-sub">
                  {fileOpChange.kind === 'copy'
                    ? t('metaAgent.proposal.copyIntoFolder', { folder: fileOpChange.to })
                    : fileOpChange.kind === 'move'
                      ? t('metaAgent.proposal.moveBetweenFolders', {
                          from: fileOpChange.from, to: fileOpChange.to,
                        })
                      /* The one thing a delete confirmation must say: is this
                         the last copy? "Removed from a folder" and "destroyed"
                         are different acts, and only one of them is undoable. */
                      : fileOpChange.forever
                        ? t('metaAgent.proposal.deleteForever', { folder: fileOpChange.from })
                        : t('metaAgent.proposal.deleteKeptIn', {
                            folder: fileOpChange.from,
                            kept: fileOpChange.keptIn.join(', '),
                          })}
                </span>
              </div>
            </div>
          )}
          {libraryEditSubject && (
            <div className="aoa-pcard-linkrow" data-testid="aoa-pcard-library-edit">
              <span className="aoa-pcard-linkrow-icon" aria-hidden>{ICON_FILE}</span>
              <div className="aoa-pcard-linkrow-text">
                <span className="aoa-pcard-linkrow-file" dir="auto">
                  {libraryEditSubject.fileName || t('metaAgent.proposal.fileFallback')}
                </span>
                <span className="aoa-pcard-linkrow-sub">
                  {libraryEditSubject.folders.length > 0
                    ? t('metaAgent.proposal.editInFolder', {
                        folder: libraryEditSubject.folders.join(' · '),
                      })
                    : t('metaAgent.proposal.editLibraryFile')}
                </span>
              </div>
            </div>
          )}
          {/* A Library-file edit shows the FINISHED file, not a line diff.
              These are real documents — an Excel workbook, a Word file — and
              the user judges the result by looking at it, then Accept saves
              that version over the original. The proposed bytes come from a
              preview endpoint that runs the exact apply path without saving,
              so what they see is what Accept writes. (Rename has no new bytes
              and keeps its old→new name row above.) */}
          {libraryEditEditsContent && (
            <LibraryEditPreview
              proposalId={proposal.proposal_id}
              fileName={libraryEditSubject?.fileName || 'file'}
              t={t}
            />
          )}
          {isEdit && !renameChange && !libraryEditEditsContent && !fileOpChange
            && !allowedModelsChange && !outputFormatsChange &&
            !defaultModelChange && !modeSliderChange && !triggersChange && (
            // No inner scroll: the diff grows to its full height so the WHOLE
            // card (rationale → what-changed → diff) scrolls as ONE region in
            // the inspector panel. A nested scroll box here traps the wheel and
            // makes the section feel unscrollable. Full text is one click away
            // via "View System Prompt".
            <DiffView left={oldText} right={newText} maxHeight={100000} />
          )}
          {isCreate && (
            <CreatePreview toolName={proposal.tool_name} payload={payloadObj} t={t} />
          )}
          {isConnection && (
            <ConnectionRow toolName={proposal.tool_name} payload={payloadObj} t={t} />
          )}
          {isDuplicate && <DuplicateRow payload={payloadObj} t={t} />}
          {isDelete && <DeletePanel payload={payloadObj} t={t} />}
          {linkFilePayload && (
            <div className="aoa-pcard-linkrow">
              <span className="aoa-pcard-linkrow-icon" aria-hidden>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none"
                     stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                  <polyline points="14 2 14 8 20 8" />
                </svg>
              </span>
              <div className="aoa-pcard-linkrow-text">
                <span className="aoa-pcard-linkrow-file" dir="auto">
                  {linkFilePayload.fileName || t('metaAgent.proposal.fileFallback')}
                </span>
                <span className="aoa-pcard-linkrow-sub">
                  {linkFilePayload.action === 'unlink'
                    ? t('metaAgent.proposal.removeFrom')
                    : t('metaAgent.proposal.attachTo')}{' '}
                  {linkFilePayload.targetType ? linkKindLabelOf(t, linkFilePayload.targetType) : t('metaAgent.proposal.linkKind.target')}
                  {linkFilePayload.targetName ? ` · ${linkFilePayload.targetName}` : ''}
                </span>
              </div>
            </div>
          )}
          {libraryLinkPayload && (
            <ConnectVisual
              sourceIcon={libraryLinkPayload.nodeType === 'folder' ? ICON_FOLDER : ICON_FILE}
              sourceName={libraryLinkPayload.nodeName || t('metaAgent.proposal.fileFallback')}
              targetIcon={
                libraryLinkPayload.targetKind === 'skill' ? ICON_SKILL
                  : libraryLinkPayload.targetKind === 'library_folder' ? ICON_FOLDER
                  : ICON_AGENT
              }
              targetName={
                libraryLinkPayload.targetName
                || linkKindLabelOf(t, libraryLinkPayload.targetKind)
              }
              verb={libraryLinkPayload.action === 'unlink'
                ? t('metaAgent.proposal.removeFrom')
                : libraryLinkPayload.action === 'file'
                ? t('metaAgent.proposal.fileInto')
                : t('metaAgent.proposal.connectTo')}
              cut={libraryLinkPayload.action === 'unlink'}
              chip={libraryLinkPayload.includeAsContext !== null ? (
                <span className="aoa-pcard-chip">
                  {libraryLinkPayload.includeAsContext
                    ? t('metaAgent.proposal.alwaysInContext')
                    : t('metaAgent.proposal.searchableOnDemand')}
                </span>
              ) : undefined}
            />
          )}
          {showsGenericPayload && <KeyValueList payload={payloadObj} t={t} />}
        </div>
      )}

      {editing && (
        <div className="aoa-pcard-body">
          {/* Same editor the system-prompt fields use elsewhere: the inline box
              for a quick tweak, plus an expand button that opens a large centred
              modal. A proposal value is often a whole system prompt, which is
              not realistically editable inside a side panel. */}
          <ExpandableTextarea
            className="aoa-pcard-edit-textarea"
            value={editedText}
            onChange={setEditedText}
            placeholder={t('metaAgent.proposal.editPlaceholder')}
            rows={Math.min(20, Math.max(8, editedText.split('\n').length + 1))}
            label={t('metaAgent.proposal.expandEditor')}
          />
        </div>
      )}

      {viewingText && (
        <PromptViewModal
          title={proposal.field === 'chatbot_system_prompt'
            ? (t('metaAgent.proposal.viewSystemPrompt', { defaultValue: 'View System Prompt' }))
            : (t('metaAgent.proposal.viewFullText', { defaultValue: 'View full text' }))}
          text={newText}
          onClose={() => setViewingText(false)}
        />
      )}

      {error && <div className="aoa-pcard-error">{error}</div>}

      {/* Footer */}
      {!isResolved && (
        <div className="aoa-pcard-footer">
          {!editing && (
            <>
              <button
                type="button"
                className="aoa-pcard-btn aoa-pcard-btn-ghost"
                data-testid="aoa-pcard-reject"
                onClick={onReject}
                disabled={busy}
              >
                {t('metaAgent.proposal.actionReject') || 'Reject'}
              </button>
              {/* Read a whole system prompt (or other long field) the way it
                  was written — a big clean modal, always one click away, so
                  review never means squinting at a diff pane. */}
              {isEdit && LONG_TEXT_FIELDS.has(proposal.field || '')
                && (newText || '').trim().length > 0 && (
                <button
                  type="button"
                  className="aoa-pcard-btn aoa-pcard-btn-ghost"
                  data-testid="aoa-pcard-view-text"
                  onClick={() => setViewingText(true)}
                  disabled={busy}
                >
                  {proposal.field === 'chatbot_system_prompt'
                    ? (t('metaAgent.proposal.viewSystemPrompt', { defaultValue: 'View System Prompt' }))
                    : (t('metaAgent.proposal.viewFullText', { defaultValue: 'View full text' }))}
                </button>
              )}
              {/* Edit is suppressed for model proposals — the user
                  must Accept or Reject the AoA's exact id; we don't
                  let them free-type a model name into the textarea. */}
              {!isModelProposal && (
                <button
                  type="button"
                  className="aoa-pcard-btn aoa-pcard-btn-ghost"
                  onClick={() => setEditing(true)}
                  disabled={busy}
                >
                  {t('metaAgent.proposal.actionEdit') || 'Edit'}
                </button>
              )}
              <button
                type="button"
                className="aoa-pcard-btn aoa-pcard-btn-primary"
                data-testid="aoa-pcard-accept"
                onClick={onAccept}
                disabled={busy}
              >
                {busy
                  ? '…'
                  : (t('metaAgent.proposal.actionAccept') || 'Accept')}
              </button>
            </>
          )}
          {editing && (
            <>
              <button
                type="button"
                className="aoa-pcard-btn aoa-pcard-btn-ghost"
                onClick={() => setEditing(false)}
                disabled={busy}
              >
                {t('metaAgent.proposal.actionCancelEdit') || 'Cancel'}
              </button>
              <button
                type="button"
                className="aoa-pcard-btn aoa-pcard-btn-primary"
                onClick={onConfirmEdit}
                disabled={busy}
              >
                {busy
                  ? '…'
                  : (t('metaAgent.proposal.actionConfirmEdit') || 'Confirm')}
              </button>
            </>
          )}
        </div>
      )}

      {isResolved && (
        <div className="aoa-pcard-resolved-line">
          {resolvedSummary(localStatus, proposal, t)}
        </div>
      )}
    </div>
  );
};


function resolvedSummary(
  status: AoAProposal['status'],
  p: AoAProposal,
  _t: (k: string, o?: Record<string, unknown>) => string,
): string {
  const field = p.field || '';
  const key = (base: string) => (field ? `${base}Field` : base);
  const args = field ? { field } : undefined;
  if (status === 'approved') return _t(key('metaAgent.proposal.summaryApplied'), args);
  if (status === 'edited') return _t(key('metaAgent.proposal.summaryEdited'), args);
  if (status === 'rejected') return _t(key('metaAgent.proposal.summaryRejected'), args);
  if (status === 'timeout') return _t(key('metaAgent.proposal.summaryTimedOut'), args);
  return '';
}

